// Session Plan page layout rules (NP-3C-7).
//
// Pure helpers — no React, no DOM — so the rules can be unit-tested and
// shared by the Session Plan preview and the Session Builder.
//
// A session plan supports 1–6 activities on one A4 page. Each layout is
// described by the number of activity cards in each row, top to bottom:
//
//   Portrait            Landscape
//   1 → [1]             1 → [1]
//   2 → [1, 1]          2 → [2]
//   3 → [3]  stacked    3 → [3]
//   4 → [2, 2]          4 → [4]
//   5 → [2, 2, 1]       5 → [3, 2]
//   6 → [2, 2, 2]       6 → [3, 3]
//
// Rows share one CSS grid of `tracks` equal columns (the least common
// multiple of the row counts), so a card in a shorter row simply spans
// more tracks: portrait 5 → the 5th card spans the full width; landscape
// 5 → the bottom two cards are 1½ times as wide as the top three. Rows
// are always equal height.
//
// Card style: "side" = diagram left, text right (the standard card);
// "stacked" = diagram above text — used only for portrait 3, where each
// card is a tall column.

export const MAX_PLAN_ACTIVITIES = 6;
export const PLAN_ACTIVITY_LIMIT_MESSAGE = "Session plans support up to 6 activities.";

const ROW_COUNTS = {
  portrait: { 1: [1], 2: [1, 1], 3: [3], 4: [2, 2], 5: [2, 2, 1], 6: [2, 2, 2] },
  landscape: { 1: [1], 2: [2], 3: [3], 4: [4], 5: [3, 2], 6: [3, 3] },
};

const gcd = (a, b) => (b ? gcd(b, a % b) : a);
const lcm = (a, b) => (a * b) / gcd(a, b);

// True when a session plan can be generated for `n` activities.
export const isPlanActivityCountSupported = (n) =>
  Number.isInteger(n) && n >= 1 && n <= MAX_PLAN_ACTIVITIES;

// Layout for `n` activities in the given orientation, or null when `n`
// is outside 1–6.
//   cols, rows   — visual grid shape (most cards in a row × number of rows)
//   rowCounts    — cards per row, top to bottom
//   tracks       — CSS grid columns
//   spans        — grid columns spanned by each card, in activity order
//   layout       — short name, e.g. "2+2+1"
//   card         — "side" | "stacked"
export const GRID_FOR_COUNT = (n, orientation = "portrait") => {
  if (!isPlanActivityCountSupported(n)) return null;
  const table = orientation === "landscape" ? ROW_COUNTS.landscape : ROW_COUNTS.portrait;
  const rowCounts = table[n];
  const tracks = rowCounts.reduce(lcm, 1);
  return {
    cols: Math.max(...rowCounts),
    rows: rowCounts.length,
    rowCounts,
    tracks,
    spans: rowCounts.flatMap((k) => Array.from({ length: k }, () => tracks / k)),
    layout: rowCounts.join("+"),
    card: orientation !== "landscape" && n === 3 ? "stacked" : "side",
  };
};
